import { crmApiFetch } from "../../services/crmApi";
import { callCoreApi } from "../../services/coreApi";

export async function getConversations({ folder, page = 1, pageSize = 20, query = "", sort = "newest", dealId, contactId } = {}) {
	const searchParams = new URLSearchParams();
	if (folder) {
		searchParams.set("folder", folder);
	}
	searchParams.set("page", String(page));
	searchParams.set("page_size", String(pageSize));
	searchParams.set("sort", sort);
	if (query.trim()) {
		searchParams.set("q", query.trim());
	}
	if (dealId) {
		searchParams.set("deal_id", String(dealId));
	}
	if (contactId) {
		searchParams.set("contact_id", String(contactId));
	}
	const payload = await callCoreApi(`/api/conversations?${searchParams.toString()}`, {
		method: "GET",
	});

	return {
		conversations: payload.conversations || [],
		sort: payload.sort || sort,
		pagination: payload.pagination || {
			page,
			page_size: pageSize,
			total: 0,
			total_pages: 0,
		},
	};
}

export async function getConversationMessages(conversationId) {
	const payload = await callCoreApi(`/api/conversations/${conversationId}/messages`, {
		method: "GET",
	});

	return payload.messages || [];
}

export async function getContactMessages(contactId) {
	if (!contactId) {
		return [];
	}

	const conversations = [];
	let page = 1;
	let totalPages = 1;

	do {
		const payload = await getConversations({
			contactId,
			page,
			pageSize: 100,
			sort: "newest",
		});
		conversations.push(...payload.conversations);
		totalPages = Math.max(Number(payload.pagination?.total_pages || 0), 1);
		page += 1;
	} while (page <= totalPages);

	const threadIds = Array.from(new Set(
		conversations
			.map((conversation) => String(conversation?.thread_id || "").trim())
			.filter(Boolean)
	));
	const messageGroups = await Promise.all(threadIds.map(getConversationMessages));
	const messages = messageGroups.flat();
	const seen = new Set();

	return messages.filter((message) => {
		const key = String(message?.graph_message_id || message?.id || "").trim();
		if (!key || seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
}

export async function getGraphConnectURL() {
	const payload = await callCoreApi("/api/graph/connect-url", {
		method: "GET",
	});

	return payload.url || "";
}

export async function getGraphStatus() {
	return callCoreApi("/api/graph/status", {
		method: "GET",
	});
}

export async function disconnectGraph() {
	return callCoreApi("/api/graph/disconnect", {
		method: "POST",
	});
}

export async function sendGraphMail({ to, subject, body }) {
	return callCoreApi("/api/graph/send-mail", {
		method: "POST",
		body: { to, subject, body },
	});
}

export async function replyGraphMessage({ messageId, body }) {
	return callCoreApi(`/api/graph/messages/${encodeURIComponent(messageId)}/reply`, {
		method: "POST",
		body: { body: body || "" },
	});
}

export async function replyAllGraphMessage({ messageId, body }) {
	return callCoreApi(`/api/graph/messages/${encodeURIComponent(messageId)}/reply-all`, {
		method: "POST",
		body: { body: body || "" },
	});
}

export async function forwardGraphMessage({ messageId, to, body }) {
	return callCoreApi(`/api/graph/messages/${encodeURIComponent(messageId)}/forward`, {
		method: "POST",
		body: { to, body: body || "" },
	});
}

export async function getPhoneCalls({ dealId, contactId } = {}) {
	const searchParams = new URLSearchParams();
	if (dealId) {
		searchParams.set("deal_id", String(dealId));
	}
	if (contactId) {
		searchParams.set("contact_id", String(contactId));
	}
	const queryString = searchParams.toString();
	const response = await crmApiFetch(`/api/phone-calls${queryString ? `?${queryString}` : ""}`, {
		method: "GET",
	});

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	if (!response.ok) {
		throw new Error(payload.error || "Failed to fetch phone calls");
	}

	return {
		calls: payload.calls || [],
		metrics: payload.metrics || {
			my_daily: 0,
			my_weekly: 0,
			team_daily: 0,
			team_weekly: 0,
			daily_goal: 60,
			weekly_goal: 300,
		},
		week_start: payload.week_start || "",
	};
}

export async function createPhoneCall(data) {
	const response = await crmApiFetch(`/api/phone-calls`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data || {}),
	});

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	if (!response.ok) {
		throw new Error(payload.error || "Failed to create phone call");
	}

	return payload;
}
